import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Filter, ShoppingBag, ArrowRight, X, Upload, ImagePlus,
  FileText, ChevronDown, Phone, Mail, User, PawPrint, Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { toast } from 'sonner';
import api from '../api/api';
import SEOMeta from '../components/SEOMeta';

/* ─────────────────────────────────────────
 * Configurazione form per categoria prodotto
 * ─────────────────────────────────────────*/
const CATEGORY_FIELDS = {
  urne: [
    { id: 'pet_weight',    label: 'Peso approssimativo del tuo amico', type: 'select', required: true,
      options: ['0–2 kg (piccolo gatto, coniglio)', '2–5 kg (gatto, cane piccolo)', '5–10 kg (cane taglia media)', '10–20 kg (cane taglia media-grande)', '20–40 kg (cane grande)', 'Oltre 40 kg (cane molto grande)'] },
    { id: 'material',      label: 'Materiale preferito', type: 'select', required: false,
      options: ['Legno artigianale (ciliegio, noce, rovere)', 'Ceramica / terracotta', 'Metallo (ottone, acciaio)', 'Pietra naturale', 'Biodegradabile con semi di fiori', 'Non ho ancora deciso'] },
    { id: 'color_finish',  label: 'Colore / finitura desiderata', type: 'text', placeholder: 'es. naturale chiaro, azzurro cielo, terracotta, personalizzato…', required: false },
    { id: 'engraving',     label: 'Testo per l\'incisione', type: 'text', placeholder: 'es. "Pallino • 2012–2026 • Per sempre nel cuore"', required: false },
    { id: 'destination',   label: 'Destinazione finale delle ceneri', type: 'select', required: false,
      options: ['Conservazione in casa', 'Dispersione in natura (mare, montagna)', 'Interramento in giardino con urna', 'Cimitero per animali', 'Non ho ancora deciso'] },
  ],
  gioielli: [
    { id: 'jewelry_type',  label: 'Tipo di gioiello', type: 'select', required: true,
      options: ['Ciondolo / pendente', 'Anello', 'Bracciale', 'Spilla / brooch', 'Orecchini', 'Non so, consigliami'] },
    { id: 'metal',         label: 'Metallo preferito', type: 'select', required: false,
      options: ['Oro giallo 18k', 'Oro bianco 18k', 'Argento 925', 'Acciaio inox (ipoallergenico)', 'Non ho preferenze'] },
    { id: 'ring_size',     label: 'Misura anello (se applicabile)', type: 'text', placeholder: 'es. 16, 17, 18 — puoi misurare con una stringa', required: false },
    { id: 'engraving',     label: 'Testo incisione (max 30 caratteri)', type: 'text', placeholder: 'es. "Luna ♥" oppure "Per sempre con me"', required: false },
    { id: 'style',         label: 'Stile preferito', type: 'select', required: false,
      options: ['Classico ed elegante', 'Moderno e minimal', 'Romantico e decorato', 'Rustico / artigianale', 'Nessuna preferenza'] },
    { id: 'quantity_jewelry', label: 'Quanti gioielli desideri?', type: 'select', required: false,
      options: ['1 pezzo', '2 pezzi (uno per ogni familiare)', '3 o più pezzi'] },
  ],
  accessori: [
    { id: 'accessory_type', label: 'Tipo di accessorio di interesse', type: 'select', required: false,
      options: ['Album fotografico personalizzato', 'Portachiavi memorial', 'Stampa artistica / poster', 'Cuscino memorial', 'Candela personalizzata', 'Pietra incisa da giardino', 'Altro (specifica nelle note)'] },
    { id: 'quantity',      label: 'Quantità desiderata', type: 'select', required: true,
      options: ['1 pezzo', '2–5 pezzi', '5–10 pezzi', 'Oltre 10 (richiesta speciale)'] },
    { id: 'personalization', label: 'Testo personalizzazione', type: 'text', placeholder: 'Nome, date, dedica o frase breve', required: false },
    { id: 'format',        label: 'Formato / dimensioni', type: 'text', placeholder: 'es. A4 per stampa, grande per cuscino, piccolo per portachiavi…', required: false },
    { id: 'style',         label: 'Stile grafico preferito', type: 'select', required: false,
      options: ['Classico e formale', 'Moderno e minimal', 'Vintage / rustico', 'Floreale / naturalistico', 'Nessuna preferenza'] },
  ],
  piante: [
    { id: 'plant_type',    label: 'Tipo di pianta', type: 'select', required: false,
      options: ['Cipresso', 'Ulivo', 'Roseto (rosa rampicante o cespuglio)', 'Albero da frutto (melo, ciliegio…)', 'Pino / Abete', 'Lavanda', 'Bambù', 'Nontiscordardimé', 'Nessuna preferenza, consigliami'] },
    { id: 'location',      label: 'Dove verrà collocata?', type: 'select', required: true,
      options: ['Giardino esterno', 'Appartamento / interno', 'Balcone o terrazzo', 'Terreno agricolo privato'] },
    { id: 'pot_size',      label: 'Dimensione vaso', type: 'select', required: false,
      options: ['Piccolo (Ø 15–20 cm)', 'Medio (Ø 25–30 cm)', 'Grande (Ø 35+ cm)', 'Senza vaso (a piena terra)'] },
    { id: 'targa',         label: 'Targa commemorativa', type: 'select', required: false,
      options: ['Sì, targa con nome e date incisi', 'Sì, targa con foto stampata', 'Sì, targa a scelta libera', 'No grazie, senza targa'] },
    { id: 'targa_text',    label: 'Testo targa (se richiesta)', type: 'text', placeholder: 'Nome, date, breve dedica…', required: false },
  ],
};

const PET_SPECIES = ['Cane', 'Gatto', 'Coniglio', 'Uccello', 'Altro'];
const FADE = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, ease: [0.22,1,0.36,1] } };

/* ─────────────────────────────────────────
 * Componente form preventivo
 * ─────────────────────────────────────────*/
function QuoteForm({ product, lang, onClose, onSuccess }) {
  const fields = CATEGORY_FIELDS[product.category] || CATEGORY_FIELDS.accessori;
  const [step, setStep] = useState(1); // 1=prodotto+animale, 2=dettagli, 3=contatti+foto
  const [form, setForm] = useState({ pet_name:'', pet_species:'', customer_name:'', customer_email:'', customer_phone:'', notes:'' });
  const [extra, setExtra] = useState({});
  const [images, setImages] = useState([]); // { name, b64, preview }
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setEx = (k, v) => setExtra(f => ({ ...f, [k]: v }));
  const productName = lang === 'en' ? product.name_en : product.name_it;

  const handleFiles = async (files) => {
    const arr = Array.from(files).slice(0, 5 - images.length);
    for (const file of arr) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 6 * 1024 * 1024) { toast.error(`${file.name} supera i 6 MB`); continue; }
      const b64 = await new Promise(res => { const r = new FileReader(); r.onload = e => res(e.target.result); r.readAsDataURL(file); });
      setImages(prev => [...prev, { name: file.name, b64, preview: b64 }]);
    }
  };

  const handleDrop = (e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email) { toast.error('Inserisci nome ed email'); return; }
    setSubmitting(true);
    try {
      const payload = {
        product_id: product.id,
        product_name: productName,
        product_category: product.category,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone || undefined,
        pet_name: form.pet_name || undefined,
        pet_species: form.pet_species || undefined,
        pet_weight: extra.pet_weight || undefined,
        extra_fields: { ...extra },
        notes: form.notes || undefined,
        images_base64: images.map(i => i.b64),
      };
      const r = await api.post('/quotes', payload);
      toast.success(r.data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante l\'invio');
    } finally { setSubmitting(false); }
  };

  const STEPS = ['Il tuo amico', 'Dettagli prodotto', 'I tuoi dati'];

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-0" data-testid="quote-form">
      {/* Step indicator */}
      <div className="flex items-center gap-1 mb-6">
        {STEPS.map((s, i) => (
          <React.Fragment key={i}>
            <button type="button" onClick={() => setStep(i+1)} className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-lg transition-colors ${step === i+1 ? 'bg-[var(--color-sage)] text-white' : 'text-[var(--color-muted-text)] hover:text-[var(--color-deep)]'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] border ${step === i+1 ? 'bg-white text-[var(--color-sage)] border-white' : step > i+1 ? 'bg-[var(--color-sage)] text-white border-[var(--color-sage)]' : 'border-current'}`}>{step > i+1 ? '✓' : i+1}</span>
              <span className="hidden sm:inline">{s}</span>
            </button>
            {i < 2 && <div className={`flex-1 h-px ${step > i+1 ? 'bg-[var(--color-sage)]' : 'bg-[var(--color-border)]'}`} />}
          </React.Fragment>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="step1" {...FADE} className="space-y-4">
            <div className="bg-[var(--color-sage-soft)] rounded-xl p-4 flex items-start gap-3">
              <Info size={16} className="text-[var(--color-sage)] mt-0.5 shrink-0" />
              <p className="text-xs text-[var(--color-deep)] leading-relaxed">Inserisci le informazioni sul tuo amico — ci aiuteranno a personalizzare il preventivo nel modo più adatto.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--color-deep)] mb-1 flex items-center gap-1.5"><PawPrint size={12} /> Nome del tuo amico *</label>
              <Input value={form.pet_name} onChange={e => set('pet_name', e.target.value)} placeholder="es. Pallino, Luna, Micio…" className="border-[var(--color-border)]" required data-testid="quote-pet-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--color-deep)] mb-1 block">Specie</label>
              <div className="flex flex-wrap gap-2">
                {PET_SPECIES.map(s => (
                  <button type="button" key={s} onClick={() => set('pet_species', s)} className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${form.pet_species === s ? 'bg-[var(--color-sage)] text-white border-[var(--color-sage)]' : 'border-[var(--color-border)] text-[var(--color-ink)] hover:border-[var(--color-sage)]'}`}>{s}</button>
                ))}
              </div>
            </div>
            {product.category === 'urne' && (
              <div>
                <label className="text-xs font-semibold text-[var(--color-deep)] mb-1 block">Peso approssimativo *</label>
                <select value={extra.pet_weight||''} onChange={e => setEx('pet_weight', e.target.value)} className="w-full border border-[var(--color-border)] rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[var(--color-sage)]" required>
                  <option value="">Seleziona…</option>
                  {['0–2 kg','2–5 kg','5–10 kg','10–20 kg','20–40 kg','Oltre 40 kg'].map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            )}
            <Button type="button" onClick={() => setStep(2)} className="w-full bg-[var(--color-deep)] hover:bg-[var(--color-brand)] text-white rounded-xl">Continua →</Button>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="step2" {...FADE} className="space-y-4">
            <p className="text-xs text-[var(--color-muted-text)] mb-2">Dettagli specifici per: <strong className="text-[var(--color-deep)]">{productName}</strong></p>
            {fields.filter(f => f.id !== 'pet_weight').map(field => (
              <div key={field.id}>
                <label className="text-xs font-semibold text-[var(--color-deep)] mb-1 block">{field.label}{field.required && ' *'}</label>
                {field.type === 'select' ? (
                  <select value={extra[field.id]||''} onChange={e => setEx(field.id, e.target.value)} className="w-full border border-[var(--color-border)] rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[var(--color-sage)]" required={field.required}>
                    <option value="">Seleziona…</option>
                    {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input value={extra[field.id]||''} onChange={e => setEx(field.id, e.target.value)} placeholder={field.placeholder} className="border-[var(--color-border)]" required={field.required} />
                )}
              </div>
            ))}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 border-[var(--color-border)]">← Indietro</Button>
              <Button type="button" onClick={() => setStep(3)} className="flex-1 bg-[var(--color-deep)] hover:bg-[var(--color-brand)] text-white rounded-xl">Continua →</Button>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="step3" {...FADE} className="space-y-4">
            {/* Dati contatto */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[var(--color-deep)] mb-1 flex items-center gap-1.5"><User size={11} /> Nome e cognome *</label>
                <Input value={form.customer_name} onChange={e => set('customer_name', e.target.value)} placeholder="Il tuo nome completo" className="border-[var(--color-border)]" required data-testid="quote-customer-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[var(--color-deep)] mb-1 flex items-center gap-1.5"><Mail size={11} /> Email *</label>
                <Input type="email" value={form.customer_email} onChange={e => set('customer_email', e.target.value)} placeholder="la-tua@email.it" className="border-[var(--color-border)]" required data-testid="quote-email" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--color-deep)] mb-1 flex items-center gap-1.5"><Phone size={11} /> Telefono (opzionale)</label>
              <Input value={form.customer_phone} onChange={e => set('customer_phone', e.target.value)} placeholder="+39 3XX XXX XXXX" className="border-[var(--color-border)]" />
            </div>

            {/* Upload immagini */}
            <div>
              <label className="text-xs font-semibold text-[var(--color-deep)] mb-2 flex items-center gap-1.5"><ImagePlus size={11} /> Foto del tuo amico (opzionale, max 5 immagini)</label>
              <div
                className="border-2 border-dashed border-[var(--color-border)] rounded-2xl p-5 text-center cursor-pointer hover:border-[var(--color-sage)] transition-colors bg-[var(--color-cream)]"
                onClick={() => fileRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                data-testid="quote-image-drop-zone"
              >
                <Upload size={20} className="mx-auto mb-2 text-[var(--color-stone)]" />
                <p className="text-xs text-[var(--color-muted-text)]">Trascina le foto qui oppure <span className="text-[var(--color-sage)] font-medium underline">clicca per selezionarle</span></p>
                <p className="text-[10px] text-[var(--color-stone)] mt-1">JPG, PNG, WEBP — max 6 MB per foto</p>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e => handleFiles(e.target.files)} />
              </div>
              {images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {images.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img.preview} alt={img.name} className="w-16 h-16 object-cover rounded-xl border border-[var(--color-border)]" />
                      <button type="button" onClick={() => setImages(prev => prev.filter((_,j) => j !== i))} className="absolute -top-1 -right-1 w-5 h-5 bg-[var(--color-terracotta)] text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Note */}
            <div>
              <label className="text-xs font-semibold text-[var(--color-deep)] mb-1 block">Note aggiuntive</label>
              <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Qualsiasi dettaglio aggiuntivo, richiesta speciale, o cosa ci vuoi dire…" className="border-[var(--color-border)] resize-none" rows={3} data-testid="quote-notes" />
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setStep(2)} className="flex-1 border-[var(--color-border)]">← Indietro</Button>
              <Button type="submit" disabled={submitting} className="flex-2 bg-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-dark)] text-white rounded-xl px-6" data-testid="quote-submit-button">
                {submitting ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Invio…</span> : <span className="flex items-center gap-2"><FileText size={14} /> Invia richiesta preventivo</span>}
              </Button>
            </div>

            <p className="text-[10px] text-center text-[var(--color-muted-text)]">Ti risponderemo entro 24–48 ore lavorative con prezzi, grafiche e tutte le opzioni disponibili.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  );
}

/* ─────────────────────────────────────────
 * Pagina principale
 * ─────────────────────────────────────────*/
export default function ShopPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [products, setProducts]       = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(true);
  const [category, setCategory]       = useState('');
  const [search, setSearch]           = useState('');
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [quoteDialog, setQuoteDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quoteSuccess, setQuoteSuccess] = useState(false);

  const categories = [
    { value: '', label: t('shop.categories.all') },
    { value: 'urne', label: t('shop.categories.urne') },
    { value: 'gioielli', label: t('shop.categories.gioielli') },
    { value: 'accessori', label: t('shop.categories.accessori') },
    { value: 'piante', label: t('shop.categories.piante') },
  ];

  useEffect(() => { fetchProducts(); }, [category, page]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (search)   params.append('search', search);
      params.append('page', page);
      params.append('limit', '12');
      const r = await api.get(`/products?${params}`);
      setProducts(r.data.items || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.pages || 1);
    } catch { toast.error(t('common.error')); }
    finally { setLoading(false); }
  };

  const openQuote = (p) => { setSelectedProduct(p); setQuoteSuccess(false); setQuoteDialog(true); };

  const categoryIcon = { urne:'⚱', gioielli:'💎', accessori:'🎁', piante:'🌿', cremazione:'🕯', ritiro:'🚗', cerimonia:'🌸', supporto:'💚' };

  return (
    <div className="bg-[var(--color-cream)] min-h-screen">
      <SEOMeta
        title="Shop — Urne, Gioielli e Accessori Memoriali"
        description="Urne artigianali, gioielli memoriali, accessori e piante commemorative per onorare la memoria del tuo animale. Richiedi un preventivo personalizzato senza impegno."
        url="/shop"
      />

      {/* Hero */}
      <section className="hero-gradient py-14 lg:py-20 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="orb-float w-80 h-80 opacity-60" style={{ left:'-3%', top:'5%', background:'var(--color-sage-soft)', filter:'blur(50px)', '--dur':'11s','--del':'0s' }} />
          <div className="orb-float w-60 h-60 opacity-40" style={{ right:'2%', top:'10%', background:'var(--color-terracotta-soft)', filter:'blur(50px)', '--dur':'9s','--del':'2s' }} />
        </div>
        <div className="container-custom relative z-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 text-[var(--color-terracotta)] text-xs font-bold uppercase tracking-widest mb-3">
              <ShoppingBag size={14} /> Shop
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl font-semibold text-[var(--color-deep)] mb-4">{t('shop.title')}</h1>
            <p className="text-[var(--color-muted-text)] max-w-xl text-lg leading-relaxed">{t('shop.subtitle')}</p>
            <div className="mt-5 inline-flex items-center gap-2 bg-[var(--color-deep)]/8 border border-[var(--color-sage)]/20 rounded-full px-4 py-2 text-sm text-[var(--color-deep)]">
              <FileText size={13} className="text-[var(--color-sage)]" />
              Ogni prodotto richiede un preventivo personalizzato — ti risponderemo entro 24 ore
            </div>
          </motion.div>
        </div>
      </section>

      <div className="container-custom py-10">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Sidebar */}
          <aside className="hidden lg:block w-52 flex-shrink-0">
            <div className="bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl p-5 sticky top-24">
              <h3 className="font-serif font-semibold text-[var(--color-deep)] mb-4 text-sm">Categorie</h3>
              <div className="space-y-1">
                {categories.map(c => (
                  <button key={c.value} onClick={() => { setCategory(c.value); setPage(1); }}
                    className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${category === c.value ? 'bg-[var(--color-sage)] text-white font-medium' : 'text-[var(--color-ink)] hover:bg-[var(--color-linen)]'}`}
                    data-testid={`filter-${c.value || 'all'}`}>
                    {categoryIcon[c.value] && <span className="mr-2">{categoryIcon[c.value]}</span>}{c.label}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          {/* Main */}
          <div className="flex-1 min-w-0">

            {/* Search + mobile filter */}
            <div className="flex gap-3 mb-6">
              <form onSubmit={e => { e.preventDefault(); setPage(1); fetchProducts(); }} className="flex-1 flex gap-2">
                <div className="relative flex-1">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-stone)]" />
                  <Input placeholder={t('shop.search')} value={search} onChange={e => setSearch(e.target.value)}
                    className="pl-9 border-[var(--color-border)] bg-[var(--color-paper)]" data-testid="shop-search-input" />
                </div>
                <Button type="submit" variant="outline" className="border-[var(--color-border)]" data-testid="shop-search-button">Cerca</Button>
              </form>

              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden border-[var(--color-border)]" data-testid="shop-filters-open-button"><Filter size={15} /></Button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-[var(--color-paper)] border-[var(--color-border)] w-60">
                  <SheetHeader><SheetTitle className="font-serif">Categorie</SheetTitle></SheetHeader>
                  <div className="space-y-1 mt-4">
                    {categories.map(c => (
                      <button key={c.value} onClick={() => { setCategory(c.value); setPage(1); }}
                        className={`w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${category === c.value ? 'bg-[var(--color-sage)] text-white' : 'hover:bg-[var(--color-linen)]'}`}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            <p className="text-sm text-[var(--color-muted-text)] mb-6">{total} prodotti disponibili</p>

            {/* Grid */}
            {loading ? (
              <div className="flex justify-center py-20"><div className="spinner" /></div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <ShoppingBag size={40} className="mx-auto text-[var(--color-stone)] mb-4" />
                <p className="text-[var(--color-muted-text)]">{t('shop.no_products')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 justify-items-center" data-testid="products-grid">
                {products.map((p, i) => {
                  const name = lang === 'en' ? p.name_en : p.name_it;
                  const desc = lang === 'en' ? p.description_en : p.description_it;
                  return (
                    <motion.div key={p.id} initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay: i*0.06, duration:0.55, ease:[0.22,1,0.36,1] }}
                      className="card-hover w-full max-w-sm bg-[var(--color-paper)] border border-[var(--color-border)] rounded-2xl overflow-hidden shadow-sm flex flex-col"
                      data-testid={`product-item-${p.id}`}>

                      {/* Logo al posto dell'immagine */}
                      <div className="aspect-[4/3] bg-gradient-to-br from-[var(--color-linen)] to-[var(--color-sage-soft)] flex items-center justify-center relative overflow-hidden">
                        <div className="absolute inset-0 opacity-5">
                          {[...Array(6)].map((_,k) => <div key={k} className="absolute rounded-full bg-[var(--color-sage)]" style={{width:60+k*20,height:60+k*20,left:`${10+k*12}%`,top:`${5+k*8}%`,opacity:0.3}} />)}
                        </div>
                        <img src="/logo.png" alt="Una Zampa in Paradiso" className="w-full max-w-[220px] h-auto object-contain rounded-xl relative z-10 drop-shadow-md" />
                        <span className="absolute bottom-2 right-2 text-xs badge-cream px-2 py-0.5 rounded-full capitalize font-medium">
                          {categoryIcon[p.category] && <span className="mr-1">{categoryIcon[p.category]}</span>}{p.category}
                        </span>
                      </div>

                      <div className="p-5 flex flex-col flex-1">
                        <h4 className="font-serif font-semibold text-[var(--color-deep)] text-base leading-snug mb-2">{name}</h4>
                        {desc && <p className="text-xs text-[var(--color-muted-text)] leading-relaxed mb-3 line-clamp-3">{desc}</p>}
                        {p.price_info && (
                          <p className="text-sm text-[var(--color-sage)] font-semibold mb-4">{p.price_info}</p>
                        )}
                        <div className="mt-auto">
                          <Button onClick={() => openQuote(p)}
                            className="w-full bg-[var(--color-terracotta)] hover:bg-[var(--color-terracotta-dark)] text-white rounded-xl btn-press text-sm font-medium"
                            data-testid={`quote-button-${p.id}`}>
                            <FileText size={14} className="mr-2" /> Richiedi preventivo
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-10">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setPage(p)}
                    className={`w-9 h-9 rounded-xl text-sm font-medium transition-colors ${page === p ? 'bg-[var(--color-sage)] text-white' : 'bg-[var(--color-paper)] border border-[var(--color-border)] hover:border-[var(--color-sage)]'}`}>
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quote Dialog */}
      <Dialog open={quoteDialog} onOpenChange={v => { setQuoteDialog(v); if (!v) setQuoteSuccess(false); }}>
        <DialogContent className="bg-[var(--color-paper)] border-[var(--color-border)] max-w-xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
          <DialogHeader className="mb-2">
            {quoteSuccess ? (
              <>
                <DialogTitle className="font-serif text-[var(--color-deep)] text-xl">Richiesta inviata!</DialogTitle>
                <DialogDescription className="text-[var(--color-muted-text)]">Ti risponderemo entro 24–48 ore con tutti i dettagli.</DialogDescription>
              </>
            ) : (
              <>
                <DialogTitle className="font-serif text-[var(--color-deep)] text-xl">Richiedi un preventivo</DialogTitle>
                <DialogDescription className="text-[var(--color-muted-text)]">
                  {selectedProduct && (lang === 'en' ? selectedProduct.name_en : selectedProduct.name_it)}
                </DialogDescription>
              </>
            )}
          </DialogHeader>

          {quoteSuccess ? (
            <motion.div {...FADE} className="text-center py-8">
              <div className="w-16 h-16 bg-[var(--color-sage-soft)] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">✦</span>
              </div>
              <h3 className="font-serif text-lg text-[var(--color-deep)] mb-3">Richiesta inviata con successo</h3>
              <p className="text-sm text-[var(--color-muted-text)] leading-relaxed mb-2">Abbiamo ricevuto la tua richiesta e ti abbiamo inviato una email di conferma.</p>
              <p className="text-sm text-[var(--color-muted-text)] leading-relaxed mb-6">Il nostro team ti risponderà entro <strong className="text-[var(--color-deep)]">24–48 ore lavorative</strong> con prezzi, opzioni grafiche e materiali personalizzati.</p>
              <Button onClick={() => setQuoteDialog(false)} className="bg-[var(--color-deep)] hover:bg-[var(--color-brand)] text-white rounded-xl px-8">Chiudi</Button>
            </motion.div>
          ) : (
            selectedProduct && (
              <QuoteForm
                product={selectedProduct}
                lang={lang}
                onClose={() => setQuoteDialog(false)}
                onSuccess={() => setQuoteSuccess(true)}
              />
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
